import type { GetStaticProps } from "next"
import type { MDXRemoteSerializeResult } from "next-mdx-remote"
import { memo } from "react"
import { Disclaimer } from "@/components/resources/components/Disclaimer"
import { ArticleHero } from "@/components/resources/components/Hero"
import { LoadingIndicatorBox } from "@/components/resources/components/LoadingIndicatorBox"
import { MarkdownContentRenderer } from "@/components/resources/components/MarkdownContentRenderer"
import { ArticleSchema } from "@/components/resources/components/Schemas"
import { type Metadata, PageMeta } from "@/components/resources/components/Seo/PageMeta"
import { TableOfContentsLayout } from "@/components/resources/components/TableOfContents"
import { NewsletterTeaser } from "@/components/resources/components/TeaserGrid/NewsletterTeaser"
import { ScrollOffsetProvider } from "@/components/resources/context/ScrollPage/ScrollPageContext"
import type { Article, Author, PostProps } from "@/components/resources/data/sanity/posts"
import { getSinglePost } from "@/components/resources/data/sanity/posts"
import type { TableOfContents } from "@/components/resources/data/sanity/tableOfContent"
import { SiteLayout } from "@/components/resources/layouts/site"
import { ArticlePage } from "@/components/resources/templates/ArticlePage"
import {
  getEngineeringAuthor,
  getEngineeringPost,
  getEngineeringPostSlugs,
} from "@/utils/engineering-blog"

/**
 * Markdown-based engineering post props
 */
interface MarkdownPostProps {
  source: "markdown"
  title: string
  description: string
  date: string
  slug: string
  tags: string[]
  image: string | null
  mdxSource: MDXRemoteSerializeResult
  tableOfContents: TableOfContents[] | null
  author: Author
  meta: Metadata
}

/**
 * Sanity CMS engineering post props
 */
interface SanityPostProps extends PostProps {
  source: "sanity"
}

type EngineeringPostPageProps = MarkdownPostProps | SanityPostProps

const EngineeringArticleContent = memo(
  ({ mdxSource }: { mdxSource: MDXRemoteSerializeResult }) => (
    <>
      <div className="max-w-3xl mx-auto px-4">
        <MarkdownContentRenderer source={mdxSource} />
        <Disclaimer />
      </div>
      <div className="flex justify-center mb-8 md:mb-12 lg:mb-16 pt-8 md:pt-12 lg:pt-16 max-w-5xl mx-auto px-4">
        <NewsletterTeaser as="section" />
      </div>
    </>
  ),
  () => true,
)

EngineeringArticleContent.displayName = "EngineeringArticleContent"

const MarkdownArticle = ({
  title,
  description,
  date,
  slug,
  tags,
  image,
  mdxSource,
  tableOfContents,
  author,
  meta,
}: MarkdownPostProps) => {
  const articleForSchema: Article = {
    id: slug,
    publishedAt: date,
    modifiedAt: date,
    slug,
    title,
    subtitle: description,
    tags,
    image: image || "",
    body: null,
    category: "engineering",
    categoryName: "Engineering",
    author,
    projectMeta: null,
    tableOfContents,
  }

  return (
    <>
      <PageMeta meta={meta} />
      <article className="mb-8 md:mb-12 lg:mb-16">
        <div className="max-w-7xl mx-auto px-4">
          <ArticleHero
            categoryName="Engineering"
            category="engineering"
            title={title}
            subtitle={description}
            image={image || undefined}
            publishedAt={date}
            author={author}
            projectMeta={null}
          />
        </div>
        {tableOfContents ? (
          <ScrollOffsetProvider scrollOffset={100}>
            <TableOfContentsLayout tableOfContents={tableOfContents}>
              <EngineeringArticleContent mdxSource={mdxSource} />
            </TableOfContentsLayout>
          </ScrollOffsetProvider>
        ) : (
          <EngineeringArticleContent mdxSource={mdxSource} />
        )}
        <ArticleSchema article={articleForSchema} />
      </article>
    </>
  )
}

const SinglePost = (props: EngineeringPostPageProps) => {
  if (props.source === "sanity") {
    if (!props.post) {
      return <LoadingIndicatorBox />
    }
    return (
      <>
        <PageMeta meta={props.meta} />
        <ArticlePage {...props.post} />
      </>
    )
  }

  return <MarkdownArticle {...props} />
}

SinglePost.getLayout = SiteLayout

export default SinglePost

export async function getStaticPaths() {
  const slugs = getEngineeringPostSlugs()

  return {
    paths: slugs.map((slug) => ({ params: { slug } })),
    fallback: "blocking",
  }
}

export const getStaticProps: GetStaticProps<EngineeringPostPageProps, { slug?: string }> = async ({
  params,
}) => {
  const { slug } = params ?? {}

  if (!slug) {
    throw new Error("Slug cannot be empty, check getStaticPaths")
  }

  // Try markdown first
  const post = await getEngineeringPost(slug)

  if (post) {
    const author = getEngineeringAuthor()

    return {
      props: {
        source: "markdown",
        title: post.title,
        description: post.description,
        date: post.date,
        slug: post.slug,
        tags: post.tags,
        image: post.image,
        mdxSource: post.mdxSource,
        tableOfContents: post.tableOfContents,
        author,
        meta: {
          title: post.title,
          description: post.description,
          image: post.image ? `https://tokenterminal.com${post.image}` : null,
          url: `https://tokenterminal.com/resources/engineering/${post.slug}`,
        },
      },
      revalidate: 180,
    }
  }

  // Fall back to Sanity CMS for older engineering posts
  const sanityProps = await getSinglePost({ category: "engineering", slug })

  if (sanityProps) {
    return {
      props: {
        source: "sanity",
        ...sanityProps,
      },
      revalidate: 180,
    }
  }

  return {
    notFound: true,
  }
}
